import React from 'react'
import {useSelector} from 'react-redux'
import {RootState} from '../../../setup'

export type ToastVariant = 'success' | 'danger' | 'warning' | 'info'

export type ToastAction = {
  label: string
  onClick: () => void
  className?: string
  disabled?: boolean
}

export type ToastMessage = {
  id: number
  message: React.ReactNode
  variant?: ToastVariant
  title?: string
  action?: ToastAction
  dismissible?: boolean
  autoDismissMs?: number
}

type ToastStackProps = {
  toasts: ToastMessage[]
  onDismiss: (id: number) => void
  className?: string
  placement?: 'top-right' | 'inline'
}

const iconByVariant: Record<ToastVariant, string> = {
  success: 'bi-check-circle-fill',
  danger: 'bi-exclamation-triangle-fill',
  warning: 'bi-exclamation-triangle-fill',
  info: 'bi-info-circle-fill',
}

const ToastStack: React.FC<ToastStackProps> = ({ toasts, onDismiss, className, placement = 'top-right' }) => {
  const sessionExpired = useSelector<RootState, boolean>(({auth}) => auth.sessionExpired ?? false)
  const toastTimeoutsRef = React.useRef<Record<number, number>>({})
  const onDismissRef = React.useRef(onDismiss)

  React.useEffect(() => {
    onDismissRef.current = onDismiss
  }, [onDismiss])

  React.useEffect(() => {
    const timeouts = toastTimeoutsRef.current

    toasts.forEach((toast) => {
      const duration = toast.autoDismissMs
      if (!duration || duration <= 0 || timeouts[toast.id]) {
        return
      }
      timeouts[toast.id] = window.setTimeout(() => {
        onDismissRef.current(toast.id)
        delete timeouts[toast.id]
      }, duration)
    })

    Object.keys(timeouts).forEach((id) => {
      const toastId = Number(id)
      const toast = toasts.find((item) => item.id === toastId)
      const duration = toast?.autoDismissMs
      if (!toast || !duration || duration <= 0) {
        window.clearTimeout(timeouts[toastId])
        delete timeouts[toastId]
      }
    })
  }, [toasts])

  React.useEffect(() => {
    return () => {
      Object.values(toastTimeoutsRef.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId)
      })
      toastTimeoutsRef.current = {}
    }
  }, [])

  if (sessionExpired || toasts.length === 0) {
    return null
  }

  const containerClassName = [
    'toast-container d-flex flex-column gap-3',
    placement === 'top-right' ? 'position-fixed top-0 end-0' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={containerClassName}
      aria-live='polite'
      aria-atomic='true'
      style={placement === 'top-right' ? { zIndex: 1090 } : undefined}
    >
      {toasts.map((toast) => {
        const variant = toast.variant ?? 'info'
        const iconClass = iconByVariant[variant] ?? iconByVariant.info
        return (
          <div key={toast.id} className={`toast show toast-${variant}`} role='alert' aria-live='assertive' aria-atomic='true'>
            <div className='toast-body d-flex gap-3 align-items-start'>
              <i className={`bi ${iconClass}`}></i>
              <div className='flex-grow-1'>
                {toast.title && <div className='fw-semibold mb-1'>{toast.title}</div>}
                <div>{toast.message}</div>
                {toast.action && (
                  <button
                    type='button'
                    className={`btn btn-sm ${toast.action.className ?? 'btn-light'} mt-3`}
                    onClick={toast.action.onClick}
                    disabled={toast.action.disabled}
                  >
                    {toast.action.label}
                  </button>
                )}
              </div>
              {toast.dismissible === false ? null : (
                <button
                  type='button'
                  className='btn-close ms-auto'
                  aria-label='Dismiss notification'
                  onClick={() => onDismiss(toast.id)}
                ></button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ToastStack
